import ExportArtifactOption from "../ExportArtifactOption";
import TableForm from "@ui/Table/Form";
import TableEditableCell, { TableInputControl } from "@ui/Table/EditableCell";
import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import * as io from "io-ts";
import cx from "classnames";
import React from "react";
import createIOSortType from "../../../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import useSortedData from "../../../../../lib/hooks/useSortedData";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../../i18n";
import type { SortableValue } from "diagnostics-lib/comparator";
import ModalForm from "@ui/Modal/Form";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import { useQuantities } from "../../../../../api/useQuantities";
import formatNumber from "@ui/i18n/formatNumber";
import Ellipsis from "@ui/Ellipsis";
import { numberValidation } from "../../../formValidationFunctions";
import type { Project } from "../../../../../api/useProjects";
import { useSetups, type Setup } from "../../../../../api/useSetups";
import { usePatch, usePost } from "../../../../../api/useCall";
import { useCanUserDoOnProject } from "../../../../canUserDo";
import useDirtyForm from "@ui/Table/useDirtyForm";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { color } from "@ui";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine, Link, More2Fill, PencilLine, UploadLine } from "@ui/IconV2/react";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import BlankStateV2 from "@ui/BlankStateV2";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Text from "@ui/Text";
import type {
  OutputModelParsingError,
  OutputModelParsingResult,
} from "../../../../../domain/parser/outputBinding/csv";
import { parse as parseOutputModelFile } from "../../../../../domain/parser/outputBinding/csv";
import { isResultErr } from "../../../../../lib/patterns/result";
import { getOutputModelParsingErrorMessage } from "../../../../../domain/parser/outputBinding/csv/errors";
import DropzoneV2 from "@ui/DropzoneV2";
import TableCard from "@ui/Table/Card";
import {
  TableCardWithoutBorder,
  TableHeaderNoPaddingT,
} from "@ui/Table/_styles/TableCardWithoutBorder";
import Menu from "@ui/Menu";
import Option, { OptionGroup } from "@ui/Option";
import Card from "@ui/Card";
import useConfirmModal from "@ui/Modal/useConfirmModal";
import useActiveAnalysesStream, {
  useSetupActiveAnalysisCount,
} from "../../../../../api/useActiveAnalysesStream";
import { spacings } from "@ui/Theme";
import { useImpactedAnalyses } from "../useAnalysisImpacted";
import type { QuantityIndex } from "../../../../../api/useQuantities";

interface ActionsProps {
  className?: string;
  onEdit: () => void;
}

const Actions = ({ className, onEdit }: ActionsProps) => (
  <ActionIcons className={className} data-cy="project-action-icons">
    <ButtonIconV2 title={t("projects.actions.edit")} data-cy="edit-project" onClick={onEdit}>
      <PencilLine size="m" />
    </ButtonIconV2>
  </ActionIcons>
);

export default React.memo(Actions);

const computeOutputs = (setup: Setup, quantityIndex: QuantityIndex) =>
  setup.simulationOutputs.map(output => ({
    id: output.id,
    modelName: output.name,
    offsetExpected: output.offsetExpected,
    quantityName: quantityIndex[output.quantityId]?.name ?? "",
    unit: quantityIndex[output.quantityId]?.unit ?? "",
  }));

type SimulationOutput = ReturnType<typeof computeOutputs>[number];
type EditableSimulationOutput = SimulationOutput & { edited?: boolean };

const useMetadata = ({
  setup,
  projectId,
  quantityIndex,
  setDirty,
}: {
  setup: Setup;
  projectId: string;
  quantityIndex: QuantityIndex;
  setDirty: (dirty: boolean, column: string) => void;
}) => {
  const canUserEditSetup = useCanUserDoOnProject("PROJECT_SETUP", projectId);
  const [editedDataKey, setEditedDataKey] = React.useState<string>();
  const outputBindings = React.useMemo(
    () => computeOutputs(setup, quantityIndex),
    [setup, quantityIndex],
  );

  const data: EditableSimulationOutput[] = React.useMemo(() => {
    if (editedDataKey === undefined) return outputBindings;
    return outputBindings.map(outputBinding => ({
      ...outputBinding,
      edited: computeRowKey(outputBinding) === editedDataKey,
    }));
  }, [editedDataKey, outputBindings]);

  const outputMetadata: { [key in Column]: ColumnMetaData<EditableSimulationOutput> } = {
    name: {
      label: t("setup.simulation.table.quantities"),
      render: b => (
        <>
          <NameContainer>
            <NameEllipsis title={b.quantityName}>{b.quantityName}</NameEllipsis>
          </NameContainer>
          {!b.edited && canUserEditSetup ? (
            <StyledActions
              onEdit={() => setEditedDataKey(computeRowKey(b))}
              className={HIDE_ON_EXPORT}
            />
          ) : null}
        </>
      ),
      minWidth: "200px",
      sortable: true,
    },
    modelName: {
      label: t("setup.simulation.table.output"),
      render: b => b.modelName,
      minWidth: "200px",
      sortable: true,
    },
    offset: {
      label: t("setup.simulation.table.offset"),
      minWidth: "100px",
      sortable: true,
      render: b => (
        <TableEditableCell<Column>
          enabled={canUserEditSetup}
          column="offset"
          dataKey={computeRowKey(b)}
          edited={b.edited}
          setEditedDataKey={setEditedDataKey}
          showEmptyMark
          renderControl={props => (
            <TableInputControl
              {...props}
              validations={[numberValidation]}
              defaultValue={String(b.offsetExpected)}
              setDirty={setDirty}
              placeholder={t("setup.simulation.table.offset")}
              type="number"
            />
          )}
        >
          {b.offsetExpected === 0 ? undefined : (
            <TooltipV2 overlay={<OverlayWrapper text={String(b.offsetExpected)} />}>
              <span>{formatNumber(b.offsetExpected, { sign: true })}</span>
            </TooltipV2>
          )}
        </TableEditableCell>
      ),
    },
    unit: {
      label: (
        <div className={labelHintContent}>
          {t("setup.simulation.table.unit")}
          <TooltipV2
            overlay={t("setup.diagnosis.observable.table.column.unit.hint")}
            className={labelHint}
          >
            <div>
              <Link size="s" />
            </div>
          </TooltipV2>
        </div>
      ),
      render: b => b.unit,
      minWidth: "100px",
      sortable: true,
    },
  };

  return { outputMetadata, editedDataKey, setEditedDataKey, data };
};

const computeRowKey = (binding: SimulationOutput) => binding.quantityName;

const columns = ["name", "modelName", "offset", "unit"] as const;
type Column = (typeof columns)[number];

const sortSelector: { [key in Column]: (b: SimulationOutput) => SortableValue | null } = {
  name: b => b.quantityName,
  modelName: b => b.modelName,
  unit: b => b.unit,
  offset: b => b.offsetExpected,
};

const useOutputBindingSearch = createGlobalState({
  storage: "session",
  key: "outputBindingSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});

const useSortOutput = createGlobalState({
  storage: "session",
  key: "setupOutputBindingSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "name",
    direction: "asc",
  },
  scoped: true,
});

const analysisStatus = new Set([
  "SIMULATION",
  "SIMULATION_ERROR",
  "DIAGNOSIS",
  "DIAGNOSIS_ERROR",
] as const);

export const DeleteOutputBindingModal = ({
  onRequestClose,
  project,
  setup,
}: {
  onRequestClose: () => void;
  project: Project;
  setup: Setup;
}) => {
  const setupId = setup.id;
  const projectId = project.id;
  const setEndpoint = usePost(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/outputs",
  );

  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const impactedAnalyses = useImpactedAnalyses({
    setups: useSetups(projectId),
    activeAnalyses,
    setupId,
    analysisStatus,
  });
  const showAdditionalMessage = setup.simulationStatus === "VALID" && impactedAnalyses.length > 0;

  const handleSubmit = React.useCallback(async () => {
    await setEndpoint({
      routeParams: { projectId, setupId },
      body: { fileName: null, outputs: [] },
    });
    return true;
  }, [projectId, setupId, setEndpoint]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={handleSubmit}
      okButtonName={t("modal.delete")}
      okButtonVariant="destructive"
    >
      <Text>{t("setup.paragraphs.model.modal.delete_output.text")}</Text>
      {showAdditionalMessage ? (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.modal.delete.analysis.additionnal_message.warning", {
            simulationValid: setup.simulationStatus === "VALID",
            diagnosisValid: setup.diagnosisStatus === "COMPLETE",
            impactedAnalysisCount: impactedAnalyses.length,
          })}
        />
      ) : null}
    </ModalForm>
  );
};

const FormSignature = io.partial({
  offset: io.string,
});

type FormSignature = io.TypeOf<typeof FormSignature>;

const ConfirmEditOutput = ({
  quantityName,
  analysesImpactedCount,
}: {
  quantityName: string;
  analysesImpactedCount: number;
}) => (
  <div>
    <Text style={{ marginBottom: spacings.m }}>
      {t("simulationOutput.edit.confirmation", quantityName)}
    </Text>
    <AdditionnalMessage
      status="WARNING"
      text={t("setup.paragraphs.model.modal.delete_model.warningText", {
        impactedAnalysisCount: analysesImpactedCount,
      })}
    />
  </div>
);

export const QuantityOutputInfo = ({
  openModal,
  project,
  setup,
  quantityIndex,
}: {
  project: Project;
  setup: Setup;
  openModal: (name: string) => void;
  quantityIndex: QuantityIndex;
}) => {
  const canUserEditSetup = useCanUserDoOnProject("PROJECT_SETUP", project.id);
  const projectId = project.id;
  const setupId = setup.id;
  const [search, setSearch] = useOutputBindingSearch({ scope: setup.id });
  const [sort, setSort] = useSortOutput({ scope: setup.id });
  const updateOutputEndpoint = usePatch(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/output/:outputId(uuid)",
  );
  const [dirty, setDirty] = useDirtyForm<string>();

  const { editedDataKey, setEditedDataKey, outputMetadata, data } = useMetadata({
    setup,
    quantityIndex,
    setDirty,
    projectId,
  });

  const handleFormClose = React.useCallback(() => setEditedDataKey(undefined), [setEditedDataKey]);

  const [filteredData] = useFilteredDatas(data, search, output => output.quantityName);

  const sortedOutputBinding: SimulationOutput[] = useSortedData(
    filteredData,
    sortSelector[sort.column],
    sort.direction,
  );

  const findDataByDataKey = React.useCallback(
    (key: string) => sortedOutputBinding.find(output => computeRowKey(output) === key),
    [sortedOutputBinding],
  );

  const cancelEditing = React.useCallback(() => {
    handleFormClose();
  }, [handleFormClose]);
  const activeAnalysisCount = useSetupActiveAnalysisCount(projectId, setupId);
  const confirm = useConfirmModal();
  const handleSubmit = React.useCallback(
    async (values: FormSignature, output: EditableSimulationOutput) => {
      if (values.offset !== undefined) {
        const isConfirmed = await confirm({
          title: t("simulationOutput.edit.confirm.title"),
          body: (
            <ConfirmEditOutput
              quantityName={output.quantityName}
              analysesImpactedCount={activeAnalysisCount}
            />
          ),
          okButtonName: t("action.confirm"),
        });

        if (!isConfirmed) return;

        await updateOutputEndpoint({
          body: {
            offsetExpected: Number(values.offset),
          },
          routeParams: {
            projectId,
            setupId,
            outputId: output.id,
          },
        });
      }
    },
    [activeAnalysisCount, confirm, projectId, setupId, updateOutputEndpoint],
  );

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  return (
    <TableCard
      title=""
      headerLeft={
        setup.simulationOutputs.length > 0 ? (
          <SearchInputV2
            data-cy="search-project"
            placeholder={t("commons.placeholder.search")}
            onChange={handleSearchChange}
            value={search}
          />
        ) : null
      }
      headerRight={
        setup.simulationOutputs.length > 0 &&
        canUserEditSetup && (
          <Menu
            arrowWidth={0}
            // gap={16}
            placement="bottom-end"
            overlay={
              <>
                <OptionGroup>
                  <ExportArtifactOption
                    projectId={projectId}
                    setupId={setup.id}
                    section="model_binding"
                  />
                  <Option closeOnClick onClick={() => openModal("replaceOutput")}>
                    <>
                      <UploadLine color={color.carbon800} />
                      {t("commons.message_card.replace")}
                    </>
                  </Option>
                  <Option closeOnClick onClick={() => openModal("deleteOutput")}>
                    <DeleteBinLine color={color.carbon800} />
                    {t("commons.message_card.delete")}
                  </Option>
                </OptionGroup>
              </>
            }
          >
            <ButtonIconV2>
              <More2Fill size="m" />
            </ButtonIconV2>
          </Menu>
        )
      }
      className={cx(TableCardWithoutBorder, TableHeaderNoPaddingT)}
    >
      {setup.simulationOutputs.length > 0 ? (
        <TableForm
          findDataByDataKey={findDataByDataKey}
          editedDataKey={editedDataKey}
          onClose={cancelEditing}
          signature={FormSignature}
          onSubmit={handleSubmit}
          dirty={dirty}
          setDirty={setDirty}
        >
          <Table
            stickyHeader
            datas={sortedOutputBinding}
            metadata={outputMetadata}
            sort={sort}
            onSort={setSort}
            computeRowKey={computeRowKey}
          />
        </TableForm>
      ) : (
        <Card>
          <StyledBlankStateV2
            title={t("setup.paragraphs.quantities.output.blank_title")}
            subtitle={
              canUserEditSetup ? t("setup.paragraphs.quantities.output.blank_subtitle") : null
            }
            onClick={canUserEditSetup ? () => openModal("importOutput") : undefined}
            buttonLabel={
              canUserEditSetup ? t("setup.paragraphs.quantities.output.import.button") : undefined
            }
          />
        </Card>
      )}
    </TableCard>
  );
};

export const ImportOutputBinding = ({
  project,
  setup,
  onRequestClose,
}: {
  onRequestClose: () => void;
  project: Project;
  setup: Setup;
}) => {
  const { addToast } = useToasts();
  const projectId = project.id;
  const setupId = setup.id;
  const setEndpoint = usePost(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/outputs",
  );
  const quantities = useQuantities(projectId);

  const [filename, setFilename] = React.useState("");
  const [isValid, setIsValid] = React.useState<boolean>();
  const [errorText, setErrorText] = React.useState<string>();
  const [errorDetails, setErrorDetails] = React.useState<OutputModelParsingError[]>();
  const [outputModelParsingResult, setOutputModelParsingResult] =
    React.useState<OutputModelParsingResult>();

  const isOutputValid = isValid && (outputModelParsingResult?.data?.length || 0) > 0;

  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const impactedAnalyses = useImpactedAnalyses({
    setups: useSetups(projectId),
    activeAnalyses,
    setupId,
    analysisStatus,
  });
  const showAdditionalMessage =
    isOutputValid && setup.simulationStatus === "VALID" && impactedAnalyses.length > 0;

  const handleDrop = React.useCallback(
    async ([file]: File[]) => {
      if (!file) return;

      /**
       * Keep track of the file name to display inside the dropzone.
       */
      setFilename(file.name);

      /**
       * Parse file according to the expected format and content rules.
       */
      const result = await parseOutputModelFile(file, {
        expectedQuantityNameValues: quantities.map(({ name }) => name),
        quantityIdByName: quantities.reduce<Record<string, string>>(
          (acc, q) => ((acc[q.name] = q.id), acc),
          {},
        ),
      });

      /**
       * Catch for parsing errors.
       * Display humain readable error message and allow for downloading the error details.
       */
      if (isResultErr(result) && result.value[0]) {
        setIsValid(false);
        setErrorText(getOutputModelParsingErrorMessage(result.value[0]));
        setErrorDetails(result.value);
        return;
      }

      /**
       * Parsing was successful.
       * Save the result in the state to be used in the submit handler.
       */
      setIsValid(true);
      setOutputModelParsingResult(result.value as OutputModelParsingResult);
    },
    [quantities],
  );

  const downloadErrorDetails = React.useCallback(() => {
    const errors = JSON.stringify(errorDetails, null, 4);
    const a = document.createElement("a");
    a.setAttribute("href", URL.createObjectURL(new Blob([errors], { type: "application/json" })));
    a.setAttribute("download", `${new Date().toISOString()}_output_model_import_errors.json`);
    a.click();
  }, [errorDetails]);

  const handleSubmit = React.useCallback(async () => {
    if (outputModelParsingResult) {
      await setEndpoint({
        routeParams: { projectId, setupId },
        body: { fileName: filename, outputs: outputModelParsingResult.data },
      });
    }

    addToast("success", t("setup.simulation.import_batch.checking"));
    onRequestClose();
    return true;
  }, [
    addToast,
    setEndpoint,
    projectId,
    setupId,
    outputModelParsingResult,
    filename,
    onRequestClose,
  ]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={isOutputValid ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.import")}
      cancellable
    >
      <DropzoneV2
        onDrop={handleDrop}
        accept=".csv"
        nameFile={filename}
        validation={isValid}
        pathSampleFile="/metroscopia_example_binding.csv"
        multiple={false}
        errorText={isValid ? undefined : errorText}
        downloadErrors={downloadErrorDetails}
      />
      {showAdditionalMessage ? (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.modal.delete.analysis.additionnal_message.warning", {
            simulationValid: setup.simulationStatus === "VALID",
            diagnosisValid: setup.diagnosisStatus === "COMPLETE",
            impactedAnalysisCount: impactedAnalyses.length,
          })}
        />
      ) : null}
    </ModalForm>
  );
};

//#region CSS
const ActionIcons = styled.div`
  display: flex;
  align-items: center;
`;

const StyledBlankStateV2 = styled(BlankStateV2)`
  padding: 25px 20px;
`;

const StyledActions = styled(Actions)`
  display: none;
  margin-left: auto;
  tr:hover & {
    display: flex;
  }
`;

const NameContainer = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: auto;
  margin-right: auto;
`;

const NameEllipsis = styled(Ellipsis)`
  padding-right: 8px;
`;

const labelHint = css`
  max-width: 200px;
`;

const labelHintContent = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  svg {
    display: block;
  }
`;
//#endregion CSS
